import React, { useEffect, useMemo, useRef } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Gesture, GestureDetector, GestureHandlerRootView } from 'react-native-gesture-handler';
import Animated, { 
  useSharedValue, 
  useAnimatedStyle, 
  withSpring,
  interpolate,
  Extrapolation,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { VStack } from '@/components/gluestack/vstack';
import { Text } from '@/components/gluestack/text';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { StartUp02Icon } from '@hugeicons-pro/core-solid-rounded';
import { SquareLock02Icon } from '@hugeicons-pro/core-stroke-rounded';
import { usePaywallError } from '@/hooks/usePaywallError';
import { useCommunity } from '@/hooks/useCommunity';
import { useWindowMeasure } from '@/hooks/useWindowMeasure';
import { ImageViewerBackground } from './ImageViewerBackground';
import { SPRING_CONFIG } from './ImageViewerConstants';

const SWIPE_THRESHOLD = 60;
const SWIPE_CLOSE_VELOCITY = 500;

interface IImageViewerContentLockedProps {
  iconUrl: string;
  onClose: () => void;
  isCarouselEnabled?: boolean;
  onSwipePrev?: () => void;
  onSwipeNext?: () => void;
}

export const ImageViewerContentLocked: React.FC<IImageViewerContentLockedProps> = ({
  iconUrl,
  onClose,
  isCarouselEnabled = false,
  onSwipePrev,
  onSwipeNext,
}) => {
  const { t } = useTranslation();
  const { navigateToPlans } = usePaywallError();
  const community = useCommunity();
  
  // Card bounds stored as shared values for worklet access
  const cardX = useSharedValue(0);
  const cardY = useSharedValue(0);
  const cardWidth = useSharedValue(0);
  const cardHeight = useSharedValue(0);
  const { ref: cardRef, measureInWindow } = useWindowMeasure();

  // Animation values for card movement
  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);

  // Stable refs to avoid gesture tree recreation
  const onCloseRef = useRef(onClose);
  const onSwipePrevRef = useRef(onSwipePrev);
  const onSwipeNextRef = useRef(onSwipeNext);
  const isCarouselEnabledShared = useSharedValue(isCarouselEnabled);
  onCloseRef.current = onClose;
  onSwipePrevRef.current = onSwipePrev;
  onSwipeNextRef.current = onSwipeNext;
  useEffect(() => { isCarouselEnabledShared.value = isCarouselEnabled; }, [isCarouselEnabled]);

  const callClose = () => onCloseRef.current?.();
  const callSwipePrev = () => onSwipePrevRef.current?.();
  const callSwipeNext = () => onSwipeNextRef.current?.();

  const handleGetPremium = () => {
    onClose();
    setTimeout(() => navigateToPlans(), 100);
  };

  // Measure card position in window coords on the UI thread (see useWindowMeasure)
  const measureCard = () => {
    measureInWindow((x, y, width, height) => {
      cardX.value = x;
      cardY.value = y;
      cardWidth.value = width;
      cardHeight.value = height;
    });
  };

  const composedGesture = useMemo(() => {
    const panGesture = Gesture.Pan()
      .minPointers(1)
      .maxPointers(1)
      .onUpdate((event) => {
        'worklet';
        translateX.value = event.translationX;
        translateY.value = event.translationY;
      })
      .onEnd((event) => {
        'worklet';
        const deltaX = event.translationX;
        const deltaY = event.translationY;

        const shouldClose =
          (event.velocityY > SWIPE_CLOSE_VELOCITY && deltaY > 60) ||
          (deltaY > 120);

        if (shouldClose) {
          scheduleOnRN(callClose);
          return;
        }

        if (isCarouselEnabledShared.value && Math.abs(deltaX) > SWIPE_THRESHOLD && Math.abs(deltaX) > Math.abs(deltaY)) {
          if (deltaX < 0) {
            scheduleOnRN(callSwipeNext);
          } else if (deltaX > 0) {
            scheduleOnRN(callSwipePrev);
          }
        }

        translateX.value = withSpring(0, SPRING_CONFIG);
        translateY.value = withSpring(0, SPRING_CONFIG);
      })
      .shouldCancelWhenOutside(true);

    const tapGesture = Gesture.Tap().onEnd((event, success) => {
      'worklet';
      if (!success) return;

      const tapX = event.absoluteX;
      const tapY = event.absoluteY;

      const isInsideCard =
        tapX >= cardX.value &&
        tapX <= cardX.value + cardWidth.value &&
        tapY >= cardY.value &&
        tapY <= cardY.value + cardHeight.value;

      if (!isInsideCard) {
        scheduleOnRN(callClose);
      }
    });

    return Gesture.Race(panGesture, tapGesture);
  }, []);

  // Animated style for card movement and fade on swipe down
  const cardAnimatedStyle = useAnimatedStyle(() => {
    // Fade out card only on swipe down (positive translateY)
    const cardOpacity = translateY.value > 0
      ? interpolate(translateY.value, [0, 150], [1, 0.2], Extrapolation.CLAMP)
      : 1;
    
    return {
      opacity: cardOpacity,
      transform: [
        { translateX: translateX.value },
        { translateY: translateY.value },
      ],
    };
  });

  return (
    <>
      {/* Blurred background using IconUrl */}
      <View className="absolute inset-0 z-0">
        <ImageViewerBackground uri={iconUrl} opacity={0.5} />
      </View>
      
      {/* Gesture handler layer */}
      <GestureHandlerRootView
        style={[
          StyleSheet.absoluteFill,
          Platform.OS === 'android' && { top: 0, left: 0, right: 0, bottom: 0 },
        ]}
      >
        <GestureDetector gesture={composedGesture}>
          <Animated.View style={StyleSheet.absoluteFill}>
            <View className="flex-1 items-center justify-center">
              <Animated.View
                style={[{ borderRadius: 16, overflow: 'hidden', marginHorizontal: 32, backgroundColor: 'rgba(0,0,0,0.7)' }, cardAnimatedStyle]}
                ref={cardRef as any}
                onLayout={measureCard}
                collapsable={false}
              >
                <VStack className="items-center gap-4 p-8">
                  <HugeIconsWrapper icon={SquareLock02Icon} size={48} className="text-primary-500" />
                  <Text className="text-center text-white/80">
                    {t('photo.locked.message', { premiumPlan: community?.premiumPlan ?? 'Premium' })}
                  </Text>
                  <ButtonWrapper
                    icon={StartUp02Icon}
                    action="primary"
                    onPress={handleGetPremium}
                    text={t('upgrade.upgrade_button')}
                    className="mt-2"
                  />
                </VStack>
              </Animated.View>
            </View>
          </Animated.View>
        </GestureDetector>
      </GestureHandlerRootView>
    </>
  );
};

